import { Box, makeStyles, IconButton, Typography } from "@material-ui/core";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import React, { FC, ReactNode } from "react";
import { useRecoilState } from "recoil";

import Footer from "@/components/Footer";
import Header, { HEADER_HEIGHT } from "@/components/Header";
import NavigationBar, { BOTTOM_NAV_CONTENT_PADDING } from "@/components/Navigation";
import { ManagementAppBar, PC_HEADER_HEIGHT } from "@/components/property/AppBar";
import { PublicHeader } from "@/components/PublicHeader";
import { Toast } from "@/components/Toast";
import { useDevice } from "@/hooks/useDevice";
import theme from "@/theme";
import { isIos } from "@/utilities/capacitorLogic";
import { NavigationPath } from "@/utilities/enums";
import { PropertyManagementDetailSnackbarState } from "@/view_models/atoms";

const SP_WIDTH = 360;
const PC_WIDTH = 800;
// PCリデザイン画面用の本体最大幅。Figma の contentColumn 960px + 左右 padding 40px×2
const PC_WIDE_WIDTH = 1040;

export const useStyles = makeStyles<unknown, StyleProps>(() => ({
  root: (props) => ({
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    backgroundColor: props.isBackGroundWhite ? theme.palette.background.paper : theme.palette.background.default,
    paddingTop: props.isIos ? `calc(env(safe-area-inset-top, 0px) + ${HEADER_HEIGHT}px)` : `${HEADER_HEIGHT}px`,
    [theme.breakpoints.up("md")]: {
      paddingTop: props.isIos ? `calc(env(safe-area-inset-top, 0px) + ${PC_HEADER_HEIGHT}px)` : `${PC_HEADER_HEIGHT}px`,
    },
  }),
  headerContainer: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1100,
  },
  backButtonContainer: {
    display: "flex",
    alignItems: "center",
  },
  backButton: (props) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    padding: `${theme.spacing(1)} 0`,
    paddingTop: props.isDense ? theme.spacing(1) : 0,
    fontSize: theme.typography.caption.fontSize,
    color: theme.palette.text.secondary,
    "&:hover": {
      color: theme.palette.text.primary,
    },
  }),
  main: (props) => ({
    padding: props.isDense ? theme.spacing(0) : theme.spacing(2),
    paddingBottom: props.disableBottomPadding ? 0 : theme.spacing(2),
    minWidth: SP_WIDTH,
    maxWidth: props.isPcFullWidth ? "100%" : props.isPcV2 ? 1232 : props.isPcWide ? PC_WIDE_WIDTH : PC_WIDTH,
    margin: props.isPcFullWidth ? 0 : "0 auto",
    width: "100%",
    flexGrow: 1,
    [theme.breakpoints.down("sm")]: {
      paddingBottom: props.disableBottomPadding ? 0 : BOTTOM_NAV_CONTENT_PADDING,
    },
    [theme.breakpoints.up("md")]: props.isPcV2
      ? { paddingLeft: 16, paddingRight: 16 }
      : props.isPcWide
        ? {
            paddingLeft: theme.spacing(5),
            paddingRight: theme.spacing(5),
          }
        : undefined,
  }),
}));

// props の型
type StyleProps = {
  isDense?: boolean;
  isPcFullWidth?: boolean;
  isPcWide?: boolean;
  isPcV2?: boolean;
  isBackGroundWhite?: boolean;
  disableBottomPadding?: boolean;
  isIos?: boolean;
};

interface BaseTemplateProps {
  headerTitle?: string;
  HeaderActionComponent?: ReactNode;
  HeaderBackIconComponent?: ReactNode;
  onBack?: () => void;
  isDense?: boolean;
  navigationPath?: NavigationPath;
  isPcFullWidth?: boolean;
  isPcWide?: boolean;
  isPcV2?: boolean;
  disableBottomPadding?: boolean;
  hideGlobalNavigation?: boolean;
  children: ReactNode;
  isBackGroundWhite?: boolean;
}

/*
 * BaseTemplateは、アプリケーションの基本的なレイアウトを提供するコンポーネントです。
 * 認証後の画面は基本的にこのテンプレートを使用します。
 * ヘッダー、メインコンテンツ、フッターを含む構造を持ち、PCとSPのレイアウトの切り替えを含まれています。
 */
export const BaseTemplate: FC<BaseTemplateProps> = ({
  navigationPath,
  headerTitle,
  HeaderActionComponent,
  HeaderBackIconComponent,
  onBack,
  isDense = false,
  isPcFullWidth = false,
  isPcWide = false,
  isPcV2 = false,
  disableBottomPadding = false,
  hideGlobalNavigation = false,
  children,
  isBackGroundWhite = false,
}) => {
  const { isMobile } = useDevice();
  const classes = useStyles({
    isDense,
    isPcFullWidth,
    isPcWide,
    isPcV2,
    isBackGroundWhite,
    disableBottomPadding,
    isIos,
  });
  const [snackbarState, setSnackbarState] = useRecoilState(PropertyManagementDetailSnackbarState);

  return (
    <Box className={classes.root}>
      <Box className={classes.headerContainer}>
        {isMobile ? (
          <Header title={headerTitle} onBack={onBack} ActionComponent={HeaderActionComponent}
            BackIconComponent={HeaderBackIconComponent} />
        ) : hideGlobalNavigation ? (
          <PublicHeader />
        ) : (
          <ManagementAppBar />
        )}
      </Box>

      <Box component="main" className={classes.main}>
        {/* PC版の戻るボタンとタイトルとアクションボタン */}
        {!isMobile && (headerTitle || onBack || HeaderActionComponent) && (
          <>
            {onBack && (
              <Box className={classes.backButtonContainer}>
                <IconButton onClick={onBack} className={classes.backButton}>
                  <ArrowBackIosIcon fontSize="small" />
                  <Typography variant="body2">戻る</Typography>
                </IconButton>
              </Box>
            )}
            <Box mt={isDense && !onBack ? 2 : 0} mb={2}>
              <Box display="flex" alignItems="center" mb={2}>
                {/* リデザイン画面（isPcWide）はページタイトルを16px（h4）で表示する */}
                <Typography variant={isPcWide ? "h4" : "h3"} style={{ flexGrow: 1 }}>
                  {headerTitle}
                </Typography>

                {HeaderActionComponent && <Box ml="auto">{HeaderActionComponent}</Box>}
              </Box>
            </Box>
          </>
        )}

        {children}
      </Box>
      {!hideGlobalNavigation && (isMobile ? navigationPath !== undefined && <NavigationBar path={navigationPath} /> : <Footer />)}
      <Toast
        open={snackbarState.isOpen}
        variant={snackbarState.status}
        message={snackbarState.message}
        onClose={() => setSnackbarState({ ...snackbarState, isOpen: false })}
      />
    </Box>
  );
};
